import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";

export default function MyListings({ onUpgrade, onView, onReviews }) {
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);

    const userData = JSON.parse(localStorage.getItem("user"));
    const user_id = userData?.personaluid;

    const [itemsPerPage, setItemsPerPage] = useState(10);
    const pageOptions = [10, 50, 100, 150, 200];

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (!user_id) { setLoading(false); return; }
        fetch(`${API_BASE}/api/get_user_listings.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id }),
        })
            .then(res => res.json())
            .then(data => { if (data.success) setListings(data.listings); setLoading(false); })
            .catch(() => setLoading(false));
    }, [user_id]);

    useEffect(() => {
        if (!window.bootstrap) return;
        const els = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const list = [...els].map(el => new window.bootstrap.Tooltip(el));
        return () => list.forEach(t => t.dispose());
    }, [listings, currentPage, search, itemsPerPage]);

    const filteredListings = listings.filter(item =>
        item.title.toLowerCase().includes(search.toLowerCase()) ||
        item.email.toLowerCase().includes(search.toLowerCase()) ||
        item.mobile.includes(search)
    );

    const totalPages = Math.ceil(filteredListings.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedListings = filteredListings.slice(startIndex, endIndex);

    const getPageNumbers = () => {
        const maxPagesToShow = 5;
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + maxPagesToShow - 1);
        if (end - start < maxPagesToShow - 1) start = Math.max(1, end - maxPagesToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };

    useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [currentPage]);
    useEffect(() => { if (currentPage > totalPages) setCurrentPage(1); }, [itemsPerPage, search, totalPages]);

    const initial = (name) => (name ? name.trim().charAt(0).toUpperCase() : "B");

    if (loading) {
        return (
            <div className="dn-list-wrap">
                <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
                    <div className="text-center">
                        <div className="dn-pulse"></div>
                        <p className="mt-3 text-secondary fw-semibold">Fetching your business listings…</p>
                    </div>
                </div>
                <style>{dnStyles}</style>
            </div>
        );
    }

    return (
        <div className="dn-list-wrap">
            <div className="dn-hero">
                <div>
                    <h4 className="fw-bold mb-1"><i className="bi bi-buildings me-2 text-primary"></i>My Listed Businesses</h4>

                    <p className="mb-0">View, manage and upgrade your business listings.</p>
                </div>
                <div className="dn-hero-count">
                    <span className="dn-count-num">{listings.length}</span>
                    <span className="dn-count-label">Total</span>
                </div>
            </div>

            {listings.length === 0 ? (
                <div className="dn-empty">
                    <i className="bi bi-shop"></i>
                    <h6 className="mt-2 fw-bold">No businesses yet</h6>
                    <p className="text-muted">You haven't listed any business.</p>
                    <a href="/add-listing" className="btn btn-primary rounded-pill px-4">
                        <i className="bi bi-plus-circle me-1"></i> Add Listing
                    </a>
                </div>
            ) : (
                <div className="dn-card">
                    <div className="dn-toolbar">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">Show</span>
                            <select className="dn-select" value={itemsPerPage}
                                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                {pageOptions.map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-muted small">entries</span>
                        </div>
                        <div className="dn-search">
                            <i className="bi bi-search"></i>
                            <input type="text" placeholder="Search business, email, phone…"
                                value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} />
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="dn-table">
                            <thead>
                                <tr>
                                    <th>#</th><th>Business</th><th>Location</th><th>Contact</th>
                                    <th>Created</th><th>Status</th><th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedListings.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="text-muted">{startIndex + index + 1}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="dn-avatar">{initial(item.title)}</div>
                                                <div style={{ minWidth: 0 }}>
                                                    <div className="fw-semibold text-dark text-truncate">{item.title}</div>
                                                    <small className="text-muted">{item.email}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-muted"><i className="bi bi-geo-alt-fill text-danger me-1"></i>{item.province}, {item.district}</td>
                                        <td className="text-muted"><i className="bi bi-telephone-fill text-success me-1"></i>{item.mobile}</td>
                                        <td className="text-muted small">{item.created_at}</td>
                                        <td>
                                            {item.states == 1
                                                ? <span className="dn-pill dn-pill-green"><i className="bi bi-check-circle-fill"></i> Approved</span>
                                                : <span className="dn-pill dn-pill-amber"><i className="bi bi-clock-fill"></i> Pending</span>}
                                        </td>
                                        <td>
                                            <div className="d-flex justify-content-center gap-2">
                                                <button className="dn-act dn-act-blue" title="View" data-bs-toggle="tooltip" onClick={() => onView(item.listinguid)}><i className="bi bi-eye-fill"></i></button>
                                                <button className="dn-act dn-act-amber" title="Upgrade" data-bs-toggle="tooltip" onClick={() => onUpgrade(item.listinguid)}><i className="bi bi-star-fill"></i></button>
                                                <button className="dn-act dn-act-green" title="Reviews" data-bs-toggle="tooltip" onClick={() => onReviews(item.listinguid)}><i className="bi bi-chat-left-text-fill"></i></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="dn-foot">
                        <span className="text-muted small">
                            Showing {startIndex + 1} to {Math.min(endIndex, filteredListings.length)} of {filteredListings.length} entries
                        </span>
                        {totalPages > 1 && (
                            <div className="dn-pager">
                                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
                                {getPageNumbers().map(p => (
                                    <button key={p} className={p === currentPage ? "active" : ""} onClick={() => setCurrentPage(p)}>{p}</button>
                                ))}
                                <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>›</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style>{dnStyles}</style>
        </div>
    );
}

const dnStyles = `
    .dn-list-wrap { padding: 4px; }
    .dn-hero { display:flex; justify-content:space-between; align-items:center; gap:12px; background:linear-gradient(135deg,#e8f0ff,#fefefe); border-left:6px solid #4a90e2; color:#0b1220; border-radius:14px; padding:18px 22px; margin-bottom:18px; box-shadow:0 4px 14px rgba(0,0,0,0.05); }
    .dn-hero h4 { font-size:1.2rem; color:#0b1220; }
    .dn-hero p { color:#6b7280; font-size:.9rem; }
    .dn-hero-count { text-align:center; background:#eef3ff; border-radius:12px; padding:8px 18px; flex:0 0 auto; }
    .dn-count-num { display:block; font-size:1.6rem; font-weight:700; line-height:1; color:#2563eb; }
    .dn-count-label { font-size:0.72rem; color:#6b7280; text-transform:uppercase; letter-spacing:.05em; }

    .dn-card { background:#fff; border-radius:16px; box-shadow:0 6px 20px rgba(0,0,0,0.06); overflow:hidden; }
    .dn-toolbar { display:flex; justify-content:space-between; align-items:center; gap:12px; padding:16px 18px; border-bottom:1px solid #f0f2f5; flex-wrap:wrap; }
    .dn-select { border:1px solid #dfe3e8; border-radius:8px; padding:6px 10px; font-size:14px; background:#fff; outline:none; cursor:pointer; }
    .dn-search { display:flex; align-items:center; gap:8px; border:1px solid #dfe3e8; border-radius:24px; padding:7px 14px; min-width:240px; }
    .dn-search i { color:#9aa1a9; }
    .dn-search input { border:0; outline:0; font-size:14px; width:100%; background:transparent; }
    .dn-table { width:100%; border-collapse:separate; border-spacing:0; }
    .dn-table thead th { background:#f7f9fc; color:#6b7280; font-size:12px; text-transform:uppercase; letter-spacing:.04em; font-weight:700; padding:12px 16px; border-bottom:1px solid #eef0f3; white-space:nowrap; }
    .dn-table tbody td { padding:14px 16px; border-bottom:1px solid #f3f5f8; vertical-align:middle; font-size:14px; }
    .dn-table tbody tr:hover { background:#f7faff; }
    .dn-avatar { width:40px; height:40px; flex:0 0 40px; border-radius:10px; background:linear-gradient(135deg,#4a90e2,#5fb3ff); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; }
    .dn-pill { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-weight:600; padding:4px 10px; border-radius:20px; white-space:nowrap; }
    .dn-pill-green { background:#e7f8ef; color:#15803d; }
    .dn-pill-amber { background:#fff4e0; color:#b45309; }
    .dn-act { width:34px; height:34px; border:0; border-radius:9px; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:.2s; }
    .dn-act:hover { transform:translateY(-2px); }
    .dn-act-blue { background:#e9f1ff; color:#2563eb; }
    .dn-act-amber { background:#fff4e0; color:#e6a700; }
    .dn-act-green { background:#e7f8ef; color:#15803d; }
    .dn-foot { display:flex; justify-content:space-between; align-items:center; gap:12px; padding:14px 18px; flex-wrap:wrap; }
    .dn-pager { display:flex; gap:6px; }
    .dn-pager button { min-width:36px; height:36px; border:1px solid #e3e7ee; background:#fff; border-radius:9px; color:#2563eb; font-weight:600; cursor:pointer; transition:.2s; }
    .dn-pager button:hover:not(:disabled) { background:#eef3ff; border-color:#2563eb; }
    .dn-pager button.active { background:#2563eb; color:#fff; border-color:#2563eb; }
    .dn-pager button:disabled { opacity:.45; cursor:not-allowed; }
    .dn-empty { text-align:center; background:#fff; border-radius:16px; padding:48px 24px; box-shadow:0 6px 20px rgba(0,0,0,0.06); }
    .dn-empty i { font-size:2.4rem; color:#c7d0dd; }
    .dn-pulse { width:60px; height:60px; border-radius:50%; background:linear-gradient(135deg,#4a90e2,#5fb3ff); animation:dnpulse 1.2s infinite ease-in-out; margin:0 auto; }
    @keyframes dnpulse { 0%{transform:scale(.85);opacity:.7} 50%{transform:scale(1);opacity:1} 100%{transform:scale(.85);opacity:.7} }
    @media (max-width:640px){ .dn-search{min-width:100%;} .dn-hero{flex-direction:column;align-items:flex-start;} }
`;
